import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, ChevronRight, Crown, ShoppingBag, IndianRupee } from "lucide-react";
import { Link } from "react-router-dom";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—";

const STATUS_COLOR = {
  active:  "bg-brand-green/10 text-brand-green border-brand-green/20",
  expired: "bg-destructive/10 text-destructive border-destructive/20",
  none:    "bg-muted text-muted-foreground",
  pending: "bg-amber-500/10 text-amber-600 border-amber-500/20",
};

export default function AdminRetailers() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/retailers", { params: filter === "all" ? {} : { status: filter } });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(needle) ||
      (r.email || "").toLowerCase().includes(needle) ||
      (r.shop_name || "").toLowerCase().includes(needle) ||
      (r.phone || "").includes(needle)
    );
  }, [rows, q]);

  return (
    <div className="space-y-6" data-testid="admin-retailers-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Super Admin</p>
          <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Retailer Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every retailer with subscription status, dates, wallet, revenue & commission earned. Click a row to open their full transaction history.
          </p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-3 text-muted-foreground" />
          <Input
            placeholder="Search name, email, shop, phone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 w-[280px]"
            data-testid="retailers-search"
          />
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList data-testid="retailer-filter">
          <TabsTrigger value="all">All ({rows.length})</TabsTrigger>
          <TabsTrigger value="active">Active subscribed</TabsTrigger>
          <TabsTrigger value="expired">Expired</TabsTrigger>
          <TabsTrigger value="pending">Pending approval</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Retailer</TableHead>
                <TableHead>Shop</TableHead>
                <TableHead>Subscription</TableHead>
                <TableHead>Wallet</TableHead>
                <TableHead className="text-right">Orders</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`retailer-row-${r.id}`}>
                  <TableCell>
                    <div className="font-medium text-sm">{r.name || "—"}</div>
                    <div className="text-[11px] text-muted-foreground">{r.email}</div>
                    <div className="text-[11px] text-muted-foreground">{r.phone}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{r.shop_name || "—"}</div>
                    {r.shop_slug && <div className="text-[10px] font-mono text-muted-foreground">/{r.shop_slug}</div>}
                    {!r.approved && (
                      <Badge variant="outline" className={STATUS_COLOR.pending + " mt-1"}>Pending approval</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_COLOR[r.subscription.status] || STATUS_COLOR.none}>
                      <Crown size={11} className="mr-1" />
                      {r.subscription.status}
                      {r.subscription.plan ? ` · ${r.subscription.plan}` : ""}
                    </Badge>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      {r.subscription.starts_at ? `${fmtDate(r.subscription.starts_at)} → ${fmtDate(r.subscription.expires_at)}` : "No subscription yet"}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{money(r.wallet_balance)}</TableCell>
                  <TableCell className="text-right"><span className="inline-flex items-center gap-1"><ShoppingBag size={11} />{r.orders}</span></TableCell>
                  <TableCell className="text-right font-medium">{money(r.revenue)}</TableCell>
                  <TableCell className="text-right text-brand-blue">{money(r.commission_earned)}</TableCell>
                  <TableCell>
                    <Link to={`/admin/retailers/${r.id}`}>
                      <Button size="sm" variant="outline" data-testid={`retailer-view-${r.id}`}>
                        View <ChevronRight size={12} className="ml-1" />
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-sm text-muted-foreground">
                    No retailers match this filter.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
